/**
 * loans.ts — REDESIGN §6, the data half of the loans screen.
 *
 * Everything here is pure and clock-free (`asOf` is always a parameter), so `test/loans.test.ts`
 * covers it without a DOM: the ranking, the header aggregates, the annuity, the end-date timeline
 * and the "what frees up when" sentence.
 *
 * ## Two sources, one shape
 *
 * REDESIGN §6 specifies a `/api/loans` endpoint, and `business/server/metrics/loans.ts` implements
 * it. Its vocabulary is the one used here — `kind: 'bank' | 'card-credit'`, `status`,
 * `LoanRate { pct, source, entries }`, `monthlyPayment { agorot, source, estimated }`, trend points
 * `{ month, debtAgorot, estimated }` — so {@link normalizeLoansPayload} maps the server body onto
 * {@link LoanRow} field for field the moment the route answers.
 *
 * Until it does, {@link loansFromAccounts} derives the same shape from `GET /api/accounts`, which
 * already returns every canonical LOAN account with its interest, its contract dates, its balances
 * and its assignment. Two figures are then COMPUTED, never invented, and both are flagged so the
 * screen labels them `מוערך` (REDESIGN §6: "Where the provider gives no monthly payment, derive it
 * … and mark it מוערך"):
 *
 *   · `monthlyPaymentAgorot` — the annuity of (balance, rate, months to `contractEnd`), source
 *     `schedule`. The transaction-history path needs the loan's rows, which only the server has;
 *   · the trend — a forward schedule projection whose points are `estimated: true` and which the
 *     chart draws dashed under the word `תחזית`. A measured 12-month history needs
 *     `balance_snapshots` (ARCHITECTURE §6): LOAN balances have no other history, because
 *     `balancePerEndDay` exists only on CHECKING rows.
 *
 * ## What counts as a loan
 *
 * ARCHITECTURE §8 `netWorth`: `loanType 'CREDIT_LIMIT'` is the overdraft facility, already inside
 * the negative checking balance, and never a loan. A facility whose `contractEnd` has passed is
 * historical — 19 of the 29 live Cal rows are — so it is `status: 'closed'`, kept out of every
 * header figure and shown in the collapsed "הלוואות שנסגרו" group with its balance intact.
 *
 * Card-issuer facilities (Cal 29, Max 2) are `קרדיט בכרטיס` and never mixed with bank loans
 * (BRIEF §9.4, REDESIGN §6).
 */

import type { Account, Agorot, DayString, MonthString } from '../../api-types.ts';
import { addMonths, monthOf, parts } from './dates.ts';

// ───────────────────────────── shapes ─────────────────────────────

/** `הכל · פרטי · עסקי` — driven by `account_assignments` (BRIEF §9.1). */
export const LOAN_SCOPES = ['all', 'business', 'personal'] as const;
export type LoanScope = (typeof LOAN_SCOPES)[number];

/** The sort switch of §6: ריבית · יתרה · תשלום חודשי · תאריך סיום. `rate` is the default. */
export const LOAN_SORTS = ['rate', 'balance', 'payment', 'end'] as const;
export type LoanSort = (typeof LOAN_SORTS)[number];

/** Bank loans and card credit are two different animals and never share a list (§6). */
export type LoanKind = 'bank' | 'card-credit';

/** How the one displayed rate was arrived at (server `resolveRate`). */
export type LoanRateSource = 'none' | 'single' | 'index_plus_margin';

/** How the monthly payment was arrived at; everything but `provider` is `מוערך`. */
export type MonthlyPaymentSource = 'provider' | 'transactions' | 'schedule' | 'none';

/** One entry of the interest structure a row expands to show. */
export interface LoanInterestEntry {
  /** `FIXD` (fixed) | `INDE` (indexed). */
  kind: string | null;
  ratePct: number | null;
  /** The entry follows an index (prime). */
  indexed: boolean;
  fromDate: DayString | null;
  toDate: DayString | null;
  /** `toDate` is in the past — a tier that no longer applies. */
  expired: boolean;
  /** Which part this entry plays in the displayed rate. */
  role: 'index' | 'margin' | 'rate' | 'other';
}

export interface LoanRate {
  pct: number | null;
  source: LoanRateSource;
  entries: LoanInterestEntry[];
}

export interface LoanRow {
  id: string;
  /** Financy `accountName`, e.g. "הלוואה ללקוחות עסקיים". */
  name: string;
  providerId: string;
  numberLast4: string | null;
  product: string | null;
  kind: LoanKind;
  /** Which system the account is assigned to (`assigned` = this business system). */
  scope: 'business' | 'personal';
  /** Positive debt in agorot — `|closingBooked|` (ARCHITECTURE §4: LOAN `booked` is positive). */
  balanceAgorot: Agorot;
  ratePct: number | null;
  rate: LoanRate | null;
  monthlyPaymentAgorot: Agorot | null;
  monthlyPaymentSource: MonthlyPaymentSource;
  /** `true` when the payment was derived rather than delivered → chip `מוערך`. */
  monthlyEstimated: boolean;
  startDate: DayString | null;
  endDate: DayString | null;
  /** Whole months from `asOf` to `endDate`, or null when the contract has no end. */
  monthsLeft: number | null;
  /** Interest payable over the next 12 months at the current balance and rate. */
  yearInterestAgorot: Agorot | null;
  /** `false` = interest-only: no payment big enough to amortise, so the balance never falls. */
  amortised: boolean;
  /** ARCHITECTURE §8: not a `CREDIT_LIMIT`, and not already past its `contractEnd`. */
  open: boolean;
}

export interface DebtPoint {
  month: MonthString;
  debtAgorot: Agorot;
  /** `true` = reconstructed or projected (dashed) · `false` = measured (solid). */
  estimated: boolean;
}

export interface LoansPayload {
  asOf: DayString;
  rows: LoanRow[];
  trend: DebtPoint[];
  /** `true` when every trend point is a projection — the chart labels it `תחזית`. */
  trendProjected: boolean;
  /** `server` = `/api/loans` · `derived` = computed here from `/api/accounts`. */
  source: 'server' | 'derived';
}

export interface LoanTotals {
  count: number;
  balanceAgorot: Agorot;
  monthlyAgorot: Agorot;
  /** Any row in the total carried a derived payment → the figure is labelled `מוערך`. */
  monthlyEstimated: boolean;
  /** Balance-weighted annual rate over the rows whose rate is known, or null. */
  weightedRatePct: number | null;
  /** How much of the debt that average covers, 0…1 — so a half-known book is not passed off as whole. */
  ratedShare: number;
  /** Σ interest over the next 12 months. */
  yearInterestAgorot: Agorot;
  /** The earliest `endDate` still ahead of `asOf`. */
  nextEnd: DayString | null;
}

// ───────────────────────────── pure arithmetic ─────────────────────────────

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/** Whole months from `from` to `to` (negative when `to` is in the past). */
export function monthsUntil(from: DayString, to: DayString): number {
  const [fy, fm, fd] = parts(from);
  const [ty, tm, td] = parts(to);
  return (ty - fy) * 12 + (tm - fm) - (td < fd ? 1 : 0);
}

/**
 * The level annuity payment of a loan: `B · r / (1 − (1+r)^−n)`, `r = ratePct/1200`.
 *
 * Returns null when the inputs cannot produce one (no balance, no term). A zero or unknown rate
 * degrades to `B / n`, which is what the live 0% relief loans and interest-free instalment plans
 * actually are. Same formula as the server's `annuityPaymentAgorot`.
 */
export function annuityPayment(balanceAgorot: Agorot, ratePct: number | null, months: number): Agorot | null {
  if (!(balanceAgorot > 0) || !(months > 0)) return null;
  if (ratePct === null || ratePct <= 0) return Math.round(balanceAgorot / months);
  const r = ratePct / 1200;
  const factor = Math.pow(1 + r, -months);
  if (factor >= 1) return Math.round(balanceAgorot / months);
  return Math.round((balanceAgorot * r) / (1 - factor));
}

export interface InterestProjection {
  agorot: Agorot | null;
  /** `false` = interest-only: the payment never covers the interest, so the debt does not fall. */
  amortised: boolean;
  monthsToZero: number | null;
}

/**
 * Interest over the next 12 months, by walking the schedule — the server's `projectInterest12m`.
 *
 * Walking beats `balance × rate` because it stops when the loan is repaid, so a loan with two
 * payments left does not report a full year of interest. With no payment (or one too small to
 * amortise) the figure is honest interest-only and `amortised` says so.
 */
export function projectInterest12m(balanceAgorot: Agorot, ratePct: number | null, monthlyPaymentAgorot: Agorot | null): InterestProjection {
  if (ratePct === null) return { agorot: null, amortised: false, monthsToZero: null };
  if (balanceAgorot <= 0 || ratePct <= 0) return { agorot: 0, amortised: monthlyPaymentAgorot !== null, monthsToZero: null };
  const r = ratePct / 1200;
  let balance = balanceAgorot;
  let total = 0;
  let amortised = false;
  let monthsToZero: number | null = null;
  for (let month = 1; month <= 12; month++) {
    const interest = balance * r;
    total += interest;
    if (monthlyPaymentAgorot === null) continue;
    const principal = monthlyPaymentAgorot - interest;
    if (principal <= 0) continue;
    amortised = true;
    balance -= principal;
    if (balance <= 0) {
      monthsToZero = month;
      break;
    }
  }
  return { agorot: Math.round(total), amortised, monthsToZero };
}

/** The remaining balance of one loan after `months` payments. */
export function balanceAfter(row: LoanRow, months: number): Agorot {
  const r = (row.ratePct ?? 0) / 1200;
  const payment = row.monthlyPaymentAgorot;
  let balance = row.balanceAgorot;
  for (let m = 0; m < months && balance > 0; m++) {
    const interest = balance * r;
    if (payment === null || payment <= interest) return Math.round(balance);
    balance = Math.max(0, balance - (payment - interest));
  }
  return Math.round(balance);
}

// ───────────────────────────── selection, ranking, totals ─────────────────────────────

export function filterLoans(rows: readonly LoanRow[], scope: LoanScope): LoanRow[] {
  if (scope === 'all') return [...rows];
  return rows.filter((row) => row.scope === scope);
}

/**
 * The §6 orders. `rate` — the default — is "הכי יקר קודם": rate descending, a row with no rate
 * last, ties broken by balance so the order is stable and meaningful.
 */
export function sortLoans(rows: readonly LoanRow[], sort: LoanSort): LoanRow[] {
  const out = [...rows];
  out.sort((a, b) => {
    switch (sort) {
      case 'balance':
        return b.balanceAgorot - a.balanceAgorot || a.name.localeCompare(b.name, 'he');
      case 'payment':
        return (b.monthlyPaymentAgorot ?? -1) - (a.monthlyPaymentAgorot ?? -1) || b.balanceAgorot - a.balanceAgorot;
      case 'end': {
        // Undated contracts sort last, and equal dates fall back to size.
        if (a.endDate === b.endDate) return b.balanceAgorot - a.balanceAgorot;
        if (!a.endDate) return 1;
        if (!b.endDate) return -1;
        return a.endDate < b.endDate ? -1 : 1;
      }
      default:
        return (b.ratePct ?? -1) - (a.ratePct ?? -1) || b.balanceAgorot - a.balanceAgorot;
    }
  });
  return out;
}

/** How many rows a group shows before `עוד N`, so a long facility list is never one wall. */
export const RANK_GROUP_LIMIT = 5;

/** The most expensive open loan across every group — what `הכי יקר קודם` exists to name. */
export function topRateRow(rows: readonly LoanRow[]): LoanRow | null {
  let best: LoanRow | null = null;
  for (const row of rows) {
    if (row.ratePct === null) continue;
    if (best === null || row.ratePct > (best.ratePct ?? -1) || (row.ratePct === best.ratePct && row.balanceAgorot > best.balanceAgorot)) {
      best = row;
    }
  }
  return best;
}

export interface LoanRankGroup {
  kind: LoanKind;
  rows: LoanRow[];
  balanceAgorot: Agorot;
}

/** The value a group ranks by under the active sort. Higher first; `null` last. */
function groupRank(rows: readonly LoanRow[], sort: LoanSort): number | null {
  let best: number | null = null;
  for (const row of rows) {
    let value: number | null;
    switch (sort) {
      case 'balance':
        value = row.balanceAgorot;
        break;
      case 'payment':
        value = row.monthlyPaymentAgorot;
        break;
      case 'end':
        // Earlier end first → negate the day number so "higher is better" holds for every sort.
        value = row.endDate === null ? null : -Number(row.endDate.replace(/-/g, ''));
        break;
      default:
        value = row.ratePct;
    }
    if (value === null) continue;
    if (best === null || value > best) best = value;
  }
  return best;
}

/**
 * §6 asks for two things that pull against each other: card facilities "grouped … never mixed with
 * bank loans", and `הכי יקר קודם` as the default order. Rows keep the server order inside a group
 * and the GROUPS are ordered by the active sort, so the expensive facilities lead instead of
 * sitting under the cheaper bank loans. (Rendering bank-then-card unconditionally is what made the
 * screen answer "מה הכי יקר" wrong.)
 */
export function rankGroups(rows: readonly LoanRow[], sort: LoanSort): LoanRankGroup[] {
  const groups: LoanRankGroup[] = [];
  for (const kind of ['bank', 'card-credit'] as const) {
    const kindRows = rows.filter((row) => row.kind === kind);
    if (kindRows.length === 0) continue;
    groups.push({ kind, rows: kindRows, balanceAgorot: kindRows.reduce((sum, row) => sum + row.balanceAgorot, 0) });
  }
  groups.sort((a, b) => {
    const ra = groupRank(a.rows, sort);
    const rb = groupRank(b.rows, sort);
    if (ra !== rb) {
      if (ra === null) return 1;
      if (rb === null) return -1;
      return rb - ra;
    }
    return b.balanceAgorot - a.balanceAgorot;
  });
  return groups;
}

/** The four header figures of §6, over whichever rows the filter left. */
export function loanTotals(rows: readonly LoanRow[], asOf: DayString): LoanTotals {
  let balanceAgorot = 0;
  let monthlyAgorot = 0;
  let monthlyEstimated = false;
  let weighted = 0;
  let ratedBase = 0;
  let yearInterestAgorot = 0;
  let nextEnd: DayString | null = null;

  for (const row of rows) {
    balanceAgorot += row.balanceAgorot;
    if (row.monthlyPaymentAgorot !== null) {
      monthlyAgorot += row.monthlyPaymentAgorot;
      if (row.monthlyEstimated) monthlyEstimated = true;
    }
    if (row.ratePct !== null && row.balanceAgorot > 0) {
      weighted += row.ratePct * row.balanceAgorot;
      ratedBase += row.balanceAgorot;
    }
    yearInterestAgorot += row.yearInterestAgorot ?? 0;
    if (row.endDate && row.endDate >= asOf && (nextEnd === null || row.endDate < nextEnd)) nextEnd = row.endDate;
  }

  return {
    count: rows.length,
    balanceAgorot,
    monthlyAgorot,
    monthlyEstimated,
    weightedRatePct: ratedBase > 0 ? round2(weighted / ratedBase) : null,
    ratedShare: balanceAgorot > 0 ? round2(ratedBase / balanceAgorot) : 0,
    yearInterestAgorot,
    nextEnd,
  };
}

// ───────────────────────────── the trend ─────────────────────────────

/**
 * Total debt over `months` months from `asOf`, by walking every loan's schedule.
 *
 * Every point is `estimated: true` — this is a projection, and the chart draws it dashed under the
 * word `תחזית`. The measured 12-month history comes from the server's `balance_snapshots`.
 */
export function projectDebt(rows: readonly LoanRow[], asOf: DayString, months = 12): DebtPoint[] {
  const start = monthOf(asOf);
  const out: DebtPoint[] = [];
  for (let m = 0; m <= months; m++) {
    let debtAgorot = 0;
    for (const row of rows) debtAgorot += balanceAfter(row, m);
    out.push({ month: addMonths(start, m), debtAgorot, estimated: true });
  }
  return out;
}

/**
 * How fast the debt is moving right now — REDESIGN §6 "יורד ב-₪X בחודש בקצב הנוכחי". The principal
 * half of this month's payments: `−(Σ payment − Σ balance × rate/12)`, so negative = falling.
 * `null` when no payment is known at all; a slope invented from one point would be a lie.
 */
export function debtMonthlyChange(rows: readonly LoanRow[]): Agorot | null {
  let principal = 0;
  let known = false;
  for (const row of rows) {
    if (row.monthlyPaymentAgorot === null) continue;
    known = true;
    const interest = row.ratePct === null ? 0 : (row.balanceAgorot * row.ratePct) / 1200;
    principal += Math.max(0, row.monthlyPaymentAgorot - interest);
  }
  // `0 - x` rather than `-x`: with nothing repaid this month the answer is +0, and a `-0` would
  // compare unequal to 0 for every caller that uses Object.is semantics.
  return known ? 0 - Math.round(principal) : null;
}

// ───────────────────────────── the end-date timeline ─────────────────────────────

export interface TimelineMark {
  row: LoanRow;
  /** 0…1 across the timeline window. */
  position: number;
}

export interface Timeline {
  fromYear: number;
  years: number[];
  marks: TimelineMark[];
  /** Loans whose contract has no end date — a footnote, never a marker. */
  undated: LoanRow[];
}

/** The horizontal strip of the next `years` years with a marker per loan (§6 "Timeline"). */
export function endTimeline(rows: readonly LoanRow[], asOf: DayString, years = 5): Timeline {
  const [y] = parts(asOf);
  const fromYear = y;
  const span = years * 12;
  const marks: TimelineMark[] = [];
  const undated: LoanRow[] = [];
  for (const row of rows) {
    if (!row.endDate) {
      undated.push(row);
      continue;
    }
    const months = monthsUntil(`${fromYear}-01-01`, row.endDate);
    if (months < 0 || months > span) continue;
    marks.push({ row, position: Math.min(1, Math.max(0, months / span)) });
  }
  marks.sort((a, b) => a.position - b.position);
  const list: number[] = [];
  for (let i = 0; i < years; i++) list.push(fromYear + i);
  return { fromYear, years: list, marks, undated };
}

export interface EndingSoon {
  /** The horizon searched (12 months) — NOT the answer. */
  horizonMonths: number;
  /**
   * Whole months to the LAST matching end date — the `{n}` of the sentence. `null` when nothing
   * ends inside the horizon. This is the event, not the window: two loans ending 01.02.2027 and
   * 10.05.2027 read "בעוד 8 חודשים", never "בעוד 12 חודשים" (the horizon read as the answer was
   * the bug, and it made the same §6 sentence mean two different things in the two systems).
   */
  withinMonths: number | null;
  count: number;
  /** Σ of the monthly payments that stop — what frees up. */
  monthlyPaymentFreedAgorot: Agorot;
  nextEndDate: DayString | null;
  nextEndMonths: number | null;
  lastEndDate: DayString | null;
}

/**
 * "בעוד {n} חודשים ייגמרו {k} הלוואות ויתפנו ₪{amount} בחודש" — how many loans end inside the next
 * `horizonMonths` months and how much monthly payment that frees. Same shape and same `withinMonths`
 * rule as the server's `endingSoonOf` (`personal/server/metrics/loans.ts`), so the sentence reads
 * identically whichever half produced it.
 */
export function endingSoon(rows: readonly LoanRow[], asOf: DayString, horizonMonths = 12): EndingSoon {
  let count = 0;
  let freed = 0;
  let nextEndDate: DayString | null = null;
  let lastEndDate: DayString | null = null;
  for (const row of rows) {
    if (!row.endDate) continue;
    const months = monthsUntil(asOf, row.endDate);
    if (months < 0 || months > horizonMonths) continue;
    count += 1;
    freed += row.monthlyPaymentAgorot ?? 0;
    if (nextEndDate === null || row.endDate < nextEndDate) nextEndDate = row.endDate;
    if (lastEndDate === null || row.endDate > lastEndDate) lastEndDate = row.endDate;
  }
  return {
    horizonMonths,
    withinMonths: lastEndDate === null ? null : Math.max(0, monthsUntil(asOf, lastEndDate)),
    count,
    monthlyPaymentFreedAgorot: freed,
    nextEndDate,
    nextEndMonths: nextEndDate === null ? null : monthsUntil(asOf, nextEndDate),
    lastEndDate,
  };
}

/** Open rows first, then closed — the screen renders the closed ones in a collapsed group. */
export function splitOpen(rows: readonly LoanRow[]): { open: LoanRow[]; closed: LoanRow[] } {
  return { open: rows.filter((row) => row.open), closed: rows.filter((row) => !row.open) };
}

// ───────────────────────────── derivation from /api/accounts ─────────────────────────────

/** Issuers whose LOAN accounts are card credit, not a bank loan (ADDENDUM, BRIEF §9.4). */
const CARD_ISSUERS: ReadonlySet<string> = new Set(['cal', 'max', 'isracard', 'amex']);

function kindOf(account: Account): LoanKind {
  if (account.loanType === 'CREDIT_CARD') return 'card-credit';
  return CARD_ISSUERS.has(account.providerId) ? 'card-credit' : 'bank';
}

/** Positive debt: LOAN `closingBooked` arrives positive, but never trust a sign (ARCHITECTURE §4). */
function debtOf(account: Account): Agorot {
  const raw = account.balances.booked ?? account.balances.expected ?? account.balances.interimBooked ?? 0;
  return Math.abs(raw);
}

/**
 * The interest an `Account` carries, read defensively.
 *
 * `api-types.ts` currently types it as ONE `InterestSummary` (`{type, ratePct, relatedIndex}`);
 * Financy delivers a LIST of tiers and the server's loans metric works on that list. Reading both
 * shapes here means this file keeps compiling and rendering whichever one the contract settles on.
 */
function rateOf(account: Account, asOf: DayString): LoanRate {
  const raw: unknown = account.interest;
  if (!raw) return { pct: null, source: 'none', entries: [] };
  const list: unknown[] = Array.isArray(raw) ? raw : [raw];
  const entries: LoanInterestEntry[] = [];
  for (const item of list) {
    if (!item || typeof item !== 'object') continue;
    const e = item as Record<string, unknown>;
    const ratePct = typeof e.ratePct === 'number' && Number.isFinite(e.ratePct) ? round2(e.ratePct) : null;
    const kind = typeof e.kind === 'string' ? e.kind : typeof e.type === 'string' ? e.type : null;
    const index = typeof e.relatedIndex === 'string' ? e.relatedIndex : null;
    const toDate = typeof e.toDate === 'string' ? e.toDate : null;
    entries.push({
      kind,
      ratePct,
      indexed: e.indexed === true || index !== null || kind === 'INDE',
      fromDate: typeof e.fromDate === 'string' ? e.fromDate : null,
      toDate,
      expired: toDate !== null && toDate < asOf,
      role: 'other',
    });
  }

  const live = entries.filter((e) => e.ratePct !== null && (!e.expired || entries.every((x) => x.expired)));
  if (live.length === 0) return { pct: null, source: 'none', entries };

  // A prime-linked tier is the INDEX; a smaller non-indexed tier beside it is a MARGIN over it, and
  // the two add up (Discount states `FIXD 0.5 + INDE 4.75 prime` → 5.25%). Two unrelated rates never
  // add: Cal states `FIXD 11.9 + INDE 5.5` with no index attached, and 11.9% is the loan's rate.
  const top = (rows: LoanInterestEntry[]): LoanInterestEntry | null =>
    rows.reduce<LoanInterestEntry | null>((best, e) => (best === null || (e.ratePct ?? 0) > (best.ratePct ?? 0) ? e : best), null);
  const indexTier = top(live.filter((e) => e.indexed));
  const plainTier = top(live.filter((e) => !e.indexed));
  if (indexTier && plainTier && (plainTier.ratePct ?? 0) < (indexTier.ratePct ?? 0)) {
    indexTier.role = 'index';
    plainTier.role = 'margin';
    return { pct: round2((indexTier.ratePct ?? 0) + (plainTier.ratePct ?? 0)), source: 'index_plus_margin', entries };
  }
  const best = top(live);
  if (!best || best.ratePct === null) return { pct: null, source: 'none', entries };
  best.role = 'rate';
  return { pct: best.ratePct, source: 'single', entries };
}

/**
 * Build the loans payload from `GET /api/accounts`. Pure: `asOf` decides what is open, what is
 * still ahead and how long every contract has left.
 */
export function loansFromAccounts(accounts: readonly Account[], asOf: DayString): LoansPayload {
  const rows: LoanRow[] = [];
  for (const account of accounts) {
    if (account.type !== 'LOAN') continue;
    // The overdraft facility is already inside the negative checking balance (ARCHITECTURE §8).
    if (account.loanType === 'CREDIT_LIMIT') continue;

    const balanceAgorot = debtOf(account);
    const endDate = account.contractEnd;
    const open = endDate === null || endDate >= asOf;
    const monthsLeft = endDate ? monthsUntil(asOf, endDate) : null;
    const rate = rateOf(account, asOf);
    const monthlyPaymentAgorot = open && monthsLeft !== null && monthsLeft > 0 ? annuityPayment(balanceAgorot, rate.pct, monthsLeft) : null;
    const interest = projectInterest12m(balanceAgorot, rate.pct, monthlyPaymentAgorot);

    rows.push({
      id: account.id,
      name: account.name,
      providerId: account.providerId,
      numberLast4: account.numberLast4,
      product: account.product,
      kind: kindOf(account),
      scope: account.assigned ? 'business' : 'personal',
      balanceAgorot,
      ratePct: rate.pct,
      rate: rate.entries.length ? rate : null,
      monthlyPaymentAgorot,
      monthlyPaymentSource: monthlyPaymentAgorot === null ? 'none' : 'schedule',
      monthlyEstimated: monthlyPaymentAgorot !== null,
      startDate: account.contractStart,
      endDate,
      monthsLeft,
      yearInterestAgorot: interest.agorot,
      amortised: interest.amortised,
      open,
    });
  }
  const openRows = rows.filter((row) => row.open);
  return { asOf, rows, trend: projectDebt(openRows, asOf, 12), trendProjected: true, source: 'derived' };
}

// ───────────────────────────── accepting the server's payload ─────────────────────────────

function num(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function str(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function pick(row: Record<string, unknown>, ...keys: string[]): unknown {
  for (const key of keys) if (row[key] !== undefined && row[key] !== null) return row[key];
  return null;
}

/**
 * Accept a `/api/loans` body, or refuse it.
 *
 * The route is specified in REDESIGN §6 and implemented in `business/server/metrics/loans.ts`, but
 * it is not yet in `business/api-types.ts`, so the web validates rather than trusts: a body that is
 * not a row list is refused and the caller derives from `/api/accounts` instead. Field names are
 * read through {@link pick} so a nested `monthlyPayment { agorot, source, estimated }` and a flat
 * `monthlyPaymentAgorot` both land, and every value is normalised to the local type — a server that
 * omits an optional field cannot crash a render.
 */
export function normalizeLoansPayload(raw: unknown, asOf: DayString): LoansPayload | null {
  if (!raw || typeof raw !== 'object') return null;
  const body = raw as Record<string, unknown>;
  const listRaw = pick(body, 'rows', 'items', 'loans');
  if (!Array.isArray(listRaw)) return null;

  const rows: LoanRow[] = [];
  for (const entry of listRaw) {
    if (!entry || typeof entry !== 'object') continue;
    const row = entry as Record<string, unknown>;
    const id = str(pick(row, 'id', 'accountId'));
    if (!id) continue;

    const balanceAgorot = Math.abs(num(pick(row, 'balanceAgorot', 'bookedAgorot')) ?? 0);
    const rateRaw = row.rate && typeof row.rate === 'object' ? (row.rate as Record<string, unknown>) : null;
    const ratePct = num(pick(row, 'ratePct')) ?? (rateRaw ? num(rateRaw.pct) : null);

    const paymentRaw = row.monthlyPayment && typeof row.monthlyPayment === 'object' ? (row.monthlyPayment as Record<string, unknown>) : null;
    const monthlyPaymentAgorot = num(pick(row, 'monthlyPaymentAgorot')) ?? (paymentRaw ? num(paymentRaw.agorot) : null);
    const paymentSource = str(pick(row, 'monthlyPaymentSource')) ?? (paymentRaw ? str(paymentRaw.source) : null);
    const paymentEstimated =
      row.monthlyEstimated === true || row.monthlyPaymentEstimated === true || (paymentRaw ? paymentRaw.estimated === true : false);

    const endDate = str(pick(row, 'endDate', 'contractEnd'));
    const status = str(pick(row, 'status'));
    const open = status !== null ? status === 'open' : endDate === null || endDate >= asOf;

    const entries: LoanInterestEntry[] = [];
    const entriesRaw = rateRaw && Array.isArray(rateRaw.entries) ? rateRaw.entries : Array.isArray(row.interest) ? row.interest : [];
    for (const item of entriesRaw) {
      if (!item || typeof item !== 'object') continue;
      const e = item as Record<string, unknown>;
      const role = e.role;
      entries.push({
        kind: str(pick(e, 'kind', 'type')),
        ratePct: num(pick(e, 'ratePct', 'percentage')),
        indexed: e.indexed === true,
        fromDate: str(e.fromDate),
        toDate: str(e.toDate),
        expired: e.expired === true,
        role: role === 'index' || role === 'margin' || role === 'rate' ? role : 'other',
      });
    }

    rows.push({
      id,
      name: str(pick(row, 'name', 'accountName')) ?? id,
      providerId: str(row.providerId) ?? 'other',
      numberLast4: str(row.numberLast4),
      product: str(row.product),
      kind: pick(row, 'kind', 'group') === 'card-credit' || row.kind === 'card' || row.group === 'card' ? 'card-credit' : 'bank',
      scope: row.scope === 'personal' || row.assigned === false ? 'personal' : 'business',
      balanceAgorot,
      ratePct,
      rate: rateRaw || entries.length ? { pct: ratePct, source: (str(rateRaw?.source) as LoanRateSource) ?? 'single', entries } : null,
      monthlyPaymentAgorot,
      monthlyPaymentSource:
        paymentSource === 'provider' || paymentSource === 'transactions' || paymentSource === 'schedule' ? paymentSource : 'none',
      monthlyEstimated: paymentEstimated,
      startDate: str(pick(row, 'startDate', 'contractStart')),
      endDate,
      monthsLeft: num(pick(row, 'monthsLeft', 'monthsRemaining')) ?? (endDate ? monthsUntil(asOf, endDate) : null),
      yearInterestAgorot: num(pick(row, 'yearInterestAgorot', 'interest12mAgorot', 'annualInterestAgorot')),
      amortised: row.amortised !== false,
      open,
    });
  }

  const trendRaw = Array.isArray(body.trend) ? body.trend : [];
  const trend: DebtPoint[] = [];
  for (const point of trendRaw) {
    if (!point || typeof point !== 'object') continue;
    const p = point as Record<string, unknown>;
    const month = str(p.month);
    const debtAgorot = num(pick(p, 'debtAgorot', 'balanceAgorot'));
    if (month === null || debtAgorot === null) continue;
    trend.push({ month, debtAgorot, estimated: p.estimated === true });
  }

  const hasTrend = trend.length >= 2;
  return {
    asOf: str(body.asOf) ?? asOf,
    rows,
    trend: hasTrend ? trend : projectDebt(rows.filter((row) => row.open), asOf, 12),
    trendProjected: !hasTrend,
    source: 'server',
  };
}
